import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { profile as fetchProfile } from '../../services/auth';
import { useAuth } from '../../context/AuthContext';
import { getAccessToken, getRefreshToken } from '../../utils/auth';
import ProfileIdentityHero from '../profile/ProfileIdentityHero';
import ProfileAboutCard from '../profile/ProfileAboutCard';
import ProfileSettingsList from '../profile/ProfileSettingsList';
import ProfileSaveBar from '../profile/ProfileSaveBar';
import ProfileSkeleton from '../profile/ProfileSkeleton';
import {
  buildProfileData,
  isProfileDirty,
  parseProfileData,
} from '../profile/profileUtils';
import { formatBytes } from '../../utils/media';

const COPY = {
  en: {
    loading: 'Loading profile…',
    loadError: 'Could not load profile. Please try again.',
    retry: 'Retry',
    nameError: 'Display name must be 3–50 characters.',
    notSignedIn: 'Your session has expired. Please sign in again.',
    discardConfirm: 'Discard unsaved changes to your profile?',
    avatarUpdated: 'Avatar updated.',
    avatarRemoved: 'Avatar removed.',
    avatarUploadError: 'Could not upload avatar. Please try again.',
  },
  zh: {
    loading: '正在加载个人信息…',
    loadError: '无法加载个人信息，请重试。',
    retry: '重试',
    nameError: '显示名称长度需为 3–50 个字符。',
    notSignedIn: '登录状态已失效，请重新登录。',
    discardConfirm: '放弃未保存的个人资料修改？',
    avatarUpdated: '头像已更新。',
    avatarRemoved: '头像已移除。',
    avatarUploadError: '头像上传失败，请重试。',
  },
};

function ErrorState({ lang, message, onRetry }) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];
  return (
    <div className="profile-v2-card profile-v2-error profile-v2-enter">
      <div className="profile-v2-error-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
          <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
      <p className="form-error">{message || t.loadError}</p>
      <div className="form-actions">
        <button type="button" onClick={onRetry}>
          {t.retry}
        </button>
      </div>
    </div>
  );
}

function ProfilePage({ lang, onLogin, onSaved, onChangePassword, onNotify }) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];
  const { user, updateProfile, refreshProfile, uploadAvatar, removeAvatar } = useAuth();
  const hasSession = Boolean(user || getAccessToken() || getRefreshToken());

  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [rawData, setRawData] = useState('{}');
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [nameError, setNameError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(null);
  const [avatarStats, setAvatarStats] = useState(null);

  const applyProfile = useCallback((data) => {
    const parsed = parseProfileData(data.data);
    const nextName = data.name || '';
    const nextBio = parsed.bio;
    setProfile(data);
    setName(nextName);
    setBio(nextBio);
    setRawData(data.data || '{}');
    setSnapshot({ name: nextName, bio: nextBio });
  }, []);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchProfile();
      applyProfile(data);
    } catch (err) {
      setLoadError(err?.message || t.loadError);
    } finally {
      setLoading(false);
    }
  }, [applyProfile, t.loadError]);

  const handleEmailVerified = useCallback(async () => {
    try {
      const data = await refreshProfile();
      applyProfile(data);
    } catch (_) {
      await loadProfile();
    }
  }, [refreshProfile, applyProfile, loadProfile]);

  useEffect(() => {
    if (!hasSession) return;
    loadProfile();
  }, [hasSession, loadProfile]);

  useEffect(() => {
    if (!justSaved) return undefined;
    const timer = setTimeout(() => setJustSaved(false), 1800);
    return () => clearTimeout(timer);
  }, [justSaved]);

  const isDirty = useMemo(
    () => isProfileDirty({ name, bio }, snapshot),
    [name, bio, snapshot],
  );

  const handleDiscard = () => {
    if (!profile) return;
    if (isDirty && String(bio || '').trim().length > 100) {
      const ok = window.confirm(t.discardConfirm);
      if (!ok) return;
    }
    applyProfile(profile);
    setNameError('');
    setSubmitError('');
    setJustSaved(false);
  };

  const handleSave = async () => {
    setNameError('');
    setSubmitError('');
    setJustSaved(false);

    const trimmedName = name.trim();
    if (trimmedName.length < 3 || trimmedName.length > 50) {
      setNameError(t.nameError);
      return;
    }

    setSaving(true);
    try {
      const updated = await updateProfile({
        name: trimmedName,
        data: buildProfileData(bio, rawData),
      });
      applyProfile(updated);
      setJustSaved(true);
      if (typeof onSaved === 'function') {
        onSaved(updated);
      }
    } catch (err) {
      const message = err?.message || t.loadError;
      if (message.includes('expired') || message.includes('login')) {
        setSubmitError(t.notSignedIn);
      } else {
        setSubmitError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarSelected = async (file) => {
    setAvatarUploading(true);
    setAvatarStats(null);
    setSubmitError('');
    try {
      const result = await uploadAvatar(file);
      const updated = result?.profile || result;
      applyProfile(updated);
      setAvatarVersion(Date.now());
      if (result?.originalBytes != null && result?.storedBytes != null) {
        setAvatarStats({
          originalBytes: result.originalBytes,
          storedBytes: result.storedBytes,
        });
        if (typeof onNotify === 'function') {
          const detail = lang === 'zh'
            ? `${formatBytes(result.originalBytes)} → ${formatBytes(result.storedBytes)}`
            : `${formatBytes(result.originalBytes)} → ${formatBytes(result.storedBytes)}`;
          onNotify(`${t.avatarUpdated} ${detail}`);
        }
      } else if (typeof onNotify === 'function') {
        onNotify(t.avatarUpdated);
      }
    } catch (err) {
      const message = err?.message || t.avatarUploadError;
      setSubmitError(message);
      if (typeof onNotify === 'function') onNotify(message);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarRemove = async () => {
    setAvatarUploading(true);
    setAvatarStats(null);
    setSubmitError('');
    try {
      const updated = await removeAvatar();
      applyProfile(updated);
      setAvatarVersion(Date.now());
      if (typeof onNotify === 'function') onNotify(t.avatarRemoved);
    } catch (err) {
      const message = err?.message || t.avatarUploadError;
      setSubmitError(message);
      if (typeof onNotify === 'function') onNotify(message);
    } finally {
      setAvatarUploading(false);
    }
  };

  if (!hasSession) {
    return (
      <section className="profile-v2-page">
        <ProfileIdentityHero lang={lang} isGuest onLogin={onLogin} />
      </section>
    );
  }

  if (loading) {
    return (
      <section className="profile-v2-page">
        <ProfileSkeleton />
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="profile-v2-page">
        <ErrorState lang={lang} message={loadError} onRetry={loadProfile} />
      </section>
    );
  }

  if (!profile) return null;

  const email = profile.email || user?.email;
  const emailVerified = Boolean(profile.emailVerified ?? user?.emailVerified);

  return (
    <section className={`profile-v2-page${isDirty ? ' has-save-bar' : ''}`}>
      <ProfileIdentityHero
        lang={lang}
        displayName={name || profile.name || user?.userName}
        email={email}
        emailVerified={emailVerified}
        memberSince={profile.createTime}
        bio={bio}
        avatarUrl={profile.avatarUrl || user?.avatarUrl}
        avatarVersion={avatarVersion || profile.updateTime}
        avatarUploading={avatarUploading}
        avatarStats={avatarStats}
        onAvatarSelected={handleAvatarSelected}
        onAvatarRemove={handleAvatarRemove}
        onAvatarError={onNotify}
      />

      <div className="profile-v2-grid">
        <ProfileAboutCard
          lang={lang}
          name={name}
          bio={bio}
          nameError={nameError}
          onNameChange={(value) => {
            setName(value);
            if (nameError) setNameError('');
          }}
          onBioChange={setBio}
          animationDelay={60}
        />
        <ProfileSettingsList
          lang={lang}
          email={email}
          emailVerified={emailVerified}
          profile={profile}
          onChangePassword={onChangePassword}
          onEmailVerified={handleEmailVerified}
          onCopyUuid={onNotify}
          animationDelay={120}
        />
      </div>

      {submitError ? (
        <p className="form-error profile-v2-form-error">{submitError}</p>
      ) : null}

      <ProfileSaveBar
        lang={lang}
        visible={isDirty}
        saving={saving}
        justSaved={justSaved}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />
    </section>
  );
}

export default ProfilePage;
